import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { Sparkles } from 'lucide-react';
import livingTreeImage from '@/assets/living-tree-showcase.png';

const copy = {
  en: {
    heading: 'Watch your words become something living.',
    subheading: 'Every message you send grows your Living Tree — a visual reminder of the relationships you\'ve chosen to nurture.',
    features: ['Fruit appears as messages are published', 'Each fruit represents a relationship', 'Your tree grows as your connections deepen'],
  },
  es: {
    heading: 'Mira cómo tus palabras se convierten en algo vivo.',
    subheading: 'Cada mensaje que envías hace crecer tu Árbol Viviente — un recordatorio visual de las relaciones que has elegido nutrir.',
    features: ['Las frutas aparecen cuando se publican mensajes', 'Cada fruta representa una relación', 'Tu árbol crece a medida que tus conexiones se profundizan'],
  },
  pt: { heading: 'Veja suas palavras se tornarem algo vivo.', subheading: 'Cada mensagem que você envia faz crescer sua Árvore Viva — um lembrete visual dos relacionamentos que você escolheu nutrir.', features: ['Frutas aparecem quando mensagens são publicadas', 'Cada fruta representa um relacionamento', 'Sua árvore cresce à medida que suas conexões se aprofundam'] },
  fr: { heading: 'Regardez vos mots devenir quelque chose de vivant.', subheading: 'Chaque message envoyé fait grandir votre Arbre Vivant — un rappel visuel des relations que vous avez choisi de nourrir.', features: ['Les fruits apparaissent lorsque les messages sont publiés', 'Chaque fruit représente une relation', 'Votre arbre grandit à mesure que vos connexions s\'approfondissent'] },
  ar: { heading: 'شاهد كلماتك تصبح شيئاً حياً.', subheading: 'كل رسالة ترسلها تُنمّي شجرتك الحية — تذكير بصري بالعلاقات التي اخترت رعايتها.', features: ['تظهر الثمار عند نشر الرسائل', 'كل ثمرة تمثل علاقة', 'شجرتك تنمو مع تعمق علاقاتك'] },
  ja: { heading: 'あなたの言葉が生きるものになるのを見てください。', subheading: '送るメッセージごとにリビングツリーが成長します — 育むことを選んだ関係の視覚的なリマインダーです。', features: ['メッセージが公開されると実が現れます', '各実は関係を表します', 'つながりが深まるとツリーが成長します'] },
  ko: { heading: '당신의 말이 살아있는 것이 되는 걸 지켜보세요.', subheading: '보내는 메시지마다 리빙 트리가 자랍니다 — 키우기로 선택한 관계의 시각적 알림입니다.', features: ['메시지가 게시되면 열매가 나타납니다', '각 열매는 관계를 나타냅니다', '연결이 깊어지면 나무가 자랍니다'] },
  id: { heading: 'Saksikan kata-katamu menjadi sesuatu yang hidup.', subheading: 'Setiap pesan yang Anda kirim menumbuhkan Living Tree — pengingat visual dari hubungan yang telah Anda pilih untuk dipelihara.', features: ['Buah muncul saat pesan dipublikasikan', 'Setiap buah mewakili sebuah hubungan', 'Pohon Anda tumbuh seiring hubungan Anda semakin dalam'] },

  hi: {
    heading: 'अपने शब्दों को कुछ जीवित होते हुए देखें।',
    subheading: 'आपके द्वारा भेजा गया प्रत्येक संदेश आपके Living Tree को बढ़ाता है — उन रिश्तों का एक दृश्य अनुस्मारक जिन्हें आपने पोषित करना चुना है।',
    features: ['Fruit appears as messages are published', 'Each fruit represents a relationship', 'Your tree grows as your connections deepen'],
  },
};

export function LivingTreeShowcase() {
  const { language } = useLanguage();
  const t = lc(copy, language);

  return (
    <section className="py-20 md:py-28 relative overflow-hidden">
      {/* Dark forest ambient */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-background via-primary/3 to-background" />

      <div className="container max-w-4xl mx-auto text-center">
        <motion.h2
          className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold billboard-flash mb-5 !leading-tight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {t.heading}
        </motion.h2>
        <motion.p
          className="text-lg text-muted-foreground mb-14 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          {t.subheading}
        </motion.p>

        {/* Living Tree Image */}
        <motion.div
          className="relative mx-auto max-w-md mb-14"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="relative rounded-3xl overflow-hidden border border-primary/20 shadow-2xl shadow-primary/10">
            <motion.img 
              src={livingTreeImage} 
              alt="Living Tree visualization - magical glowing tree with fruits representing messages"
              className="w-full h-auto object-cover"
              animate={{
                scale: [1, 1.02, 1],
                filter: [
                  'brightness(1) drop-shadow(0 0 8px hsl(var(--primary) / 0.2))',
                  'brightness(1.06) drop-shadow(0 0 20px hsl(var(--primary) / 0.4))',
                  'brightness(1) drop-shadow(0 0 8px hsl(var(--primary) / 0.2))',
                ],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            {/* Breathing glow overlay */}
            <motion.div
              className="absolute inset-0 pointer-events-none rounded-3xl"
              animate={{
                boxShadow: [
                  'inset 0 0 30px hsl(var(--primary) / 0.05)',
                  'inset 0 0 60px hsl(var(--primary) / 0.15)',
                  'inset 0 0 30px hsl(var(--primary) / 0.05)',
                ],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/20 via-transparent to-background/10 pointer-events-none" />
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
          {t.features.map((feat, i) => (
            <motion.div
              key={i}
              className="flex items-start gap-3 text-left p-4"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 + i * 0.1 }}
            >
              <Sparkles className="h-5 w-5 text-secondary mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground leading-relaxed">{feat}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
